/**
 * Search, filtering, counters and display helpers for the tasks board — the
 * single source of truth for "which tasks does this query show, and how is a
 * task written on screen?".
 *
 * As with the fleet screens, the rules live here rather than inside the
 * components: the board stays presentational and every rule can be exercised
 * directly by the healthcheck.
 *
 * Dates are compared as `YYYY-MM-DD` strings. That is deliberate — the keys
 * sort lexicographically, so "this week" is a pair of string comparisons and
 * never a time-zone question.
 */
import { ANY } from './fleet-filters';
import type { Any } from './fleet-filters';
import { canViewInternalNotes } from './permissions';
import {
  TASK_PRIORITIES,
  TASK_PRIORITY_LABELS,
  TASK_PRIORITY_RANK,
  TASK_STATUSES,
  isActiveTask,
  normalizeTaskStatus,
  taskStatusLabel,
} from '../types/task';
import type { ManualContact, Task, TaskPriority, TaskStatusId, TaskType } from '../types/task';
import type { Contact } from '../types/contact';
import type { User } from '../types/user';

// ── Calendar keys ────────────────────────────────────────────────────

const pad = (value: number): string => String(value).padStart(2, '0');

/** The local calendar day of a moment, as the `YYYY-MM-DD` key tasks store. */
export function toDateKey(value: number | Date = Date.now()): string {
  const date = value instanceof Date ? value : new Date(value);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** The key `days` days after `value` — negative values step backwards. */
export function shiftDateKey(value: number | Date, days: number): string {
  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  date.setDate(date.getDate() + days);
  return toDateKey(date);
}

/** The date ranges the board offers above the grid. */
export const TASK_DATE_RANGES = ['all', 'today', 'tomorrow', 'week', 'past'] as const;

export type TaskDateRange = (typeof TASK_DATE_RANGES)[number];

export const TASK_DATE_RANGE_LABELS: Record<TaskDateRange, string> = {
  all: 'כל התאריכים',
  today: 'היום',
  tomorrow: 'מחר',
  week: 'השבוע הקרוב',
  past: 'עבר',
};

/** True when a task's day falls inside the requested range. */
export function matchesDateRange(
  task: Task,
  range: TaskDateRange,
  now: number = Date.now(),
): boolean {
  if (range === 'all') return true;
  const today = toDateKey(now);
  if (range === 'today') return task.date === today;
  if (range === 'tomorrow') return task.date === shiftDateKey(now, 1);
  if (range === 'past') return task.date < today;
  return task.date >= today && task.date <= shiftDateKey(now, 6);
}

// ── Query parsing ────────────────────────────────────────────────────

export interface TaskSearchQuery {
  /** Exactly what was typed, kept for rendering the empty state. */
  raw: string;
  /** Lower-cased and trimmed — the substring the fields are matched against. */
  text: string;
  /** Digits only, for phone lookups. */
  digits: string;
}

export function parseTaskSearch(raw: string | null | undefined): TaskSearchQuery {
  const source = String(raw ?? '');
  const text = source.trim().toLowerCase();
  return { raw: source, text, digits: text.replace(/\D/g, '') };
}

const norm = (value: string | null | undefined): string => String(value ?? '').toLowerCase();

const digitsOf = (value: string | null | undefined): string =>
  String(value ?? '').replace(/\D/g, '');

// ── Lookups shared by the board and the search ───────────────────────

/** Extra records the search and the cards resolve names through. */
export interface TaskLookups {
  contacts?: readonly Contact[];
  taskTypes?: readonly TaskType[];
  now?: number;
}

/** The contact a task points at, whichever way it was captured. */
export function contactOf(
  task: Task,
  contacts: readonly Contact[] = [],
): ManualContact | null {
  if (task.contactId) {
    const saved = contacts.find((contact) => contact.id === task.contactId);
    if (saved) return { name: saved.fullName, phone: saved.primaryPhone, role: saved.role };
  }
  if (task.manualContact?.name || task.manualContact?.phone) return task.manualContact;
  return null;
}

/** The catalogue name of a task's type, or an empty string when unknown. */
export function taskTypeName(taskTypeId: string, taskTypes: readonly TaskType[] = []): string {
  return taskTypes.find((type) => type.id === taskTypeId)?.name ?? '';
}

export function matchesTaskSearch(
  task: Task,
  query: TaskSearchQuery,
  lookups: TaskLookups = {},
): boolean {
  if (!query.text) return true;
  const contact = contactOf(task, lookups.contacts ?? []);
  if (query.digits.length >= 3 && digitsOf(contact?.phone).includes(query.digits)) return true;
  const fields = [
    task.title,
    task.description,
    task.destinationAddress,
    task.operationalNotes,
    contact?.name,
    contact?.role,
    contact?.phone,
    taskTypeName(task.taskTypeId, lookups.taskTypes ?? []),
    TASK_PRIORITY_LABELS[task.priority],
    taskStatusLabel(task.statusId),
  ];
  return fields.some((field) => norm(field).includes(query.text));
}

// ── Filtering ────────────────────────────────────────────────────────

export interface TaskFilters {
  text: string;
  priority: TaskPriority | Any;
  taskTypeId: string | Any;
  statusId: TaskStatusId | Any;
  dateRange: TaskDateRange;
}

export const EMPTY_TASK_FILTERS: TaskFilters = {
  text: '',
  priority: ANY,
  taskTypeId: ANY,
  statusId: ANY,
  dateRange: 'all',
};

/** Chronological board order: day, then start time, then the louder task. */
export function sortTasks(tasks: readonly Task[]): Task[] {
  return [...tasks].sort((a, b) => {
    if (a.date !== b.date) return a.date.localeCompare(b.date);
    if (a.startTime !== b.startTime) return a.startTime.localeCompare(b.startTime);
    return TASK_PRIORITY_RANK[b.priority] - TASK_PRIORITY_RANK[a.priority];
  });
}

export function filterTasks(
  tasks: readonly Task[],
  filters: Partial<TaskFilters> = {},
  lookups: TaskLookups = {},
): Task[] {
  const merged = { ...EMPTY_TASK_FILTERS, ...filters };
  const now = lookups.now ?? Date.now();
  const query = parseTaskSearch(merged.text);
  return sortTasks(
    tasks.filter((task) => {
      if (merged.priority !== ANY && task.priority !== merged.priority) return false;
      if (merged.taskTypeId !== ANY && task.taskTypeId !== merged.taskTypeId) return false;
      // Both sides go through the normaliser: a row an older build filed as
      // "ממתינה לשיבוץ" is found under בביצוע, and a saved filter still asking
      // for that retired status narrows to the same board instead of an empty
      // one.
      if (
        merged.statusId !== ANY &&
        normalizeTaskStatus(task.statusId) !== normalizeTaskStatus(merged.statusId)
      ) {
        return false;
      }
      if (!matchesDateRange(task, merged.dateRange, now)) return false;
      return matchesTaskSearch(task, query, lookups);
    }),
  );
}

// ── Counters ─────────────────────────────────────────────────────────

export function taskPriorityCounts(
  tasks: readonly Task[],
): Record<TaskPriority | Any, number> {
  const counts: Record<string, number> = { [ANY]: tasks.length };
  for (const priority of TASK_PRIORITIES) counts[priority] = 0;
  for (const task of tasks) counts[task.priority] = (counts[task.priority] ?? 0) + 1;
  return counts as Record<TaskPriority | Any, number>;
}

export function taskStatusCounts(tasks: readonly Task[]): Record<TaskStatusId | Any, number> {
  const counts: Record<string, number> = { [ANY]: tasks.length };
  for (const status of TASK_STATUSES) counts[status] = 0;
  for (const task of tasks) {
    const status = normalizeTaskStatus(task.statusId);
    counts[status] = (counts[status] ?? 0) + 1;
  }
  return counts as Record<TaskStatusId | Any, number>;
}

export interface TaskSummary {
  all: number;
  today: number;
  open: number;
  urgent: number;
}

/** The four numbers the board leads with. */
export function taskSummary(tasks: readonly Task[], now: number = Date.now()): TaskSummary {
  const today = toDateKey(now);
  return {
    all: tasks.length,
    today: tasks.filter((task) => task.date === today).length,
    open: tasks.filter(isActiveTask).length,
    urgent: tasks.filter((task) => task.priority === 'urgent' && isActiveTask(task)).length,
  };
}

// ── Display helpers ──────────────────────────────────────────────────

/** `היום` / `מחר` / `אתמול`, falling back to `DD/MM`. */
export function formatTaskDate(dateKey: string, now: number = Date.now()): string {
  if (!dateKey) return '';
  if (dateKey === toDateKey(now)) return 'היום';
  if (dateKey === shiftDateKey(now, 1)) return 'מחר';
  if (dateKey === shiftDateKey(now, -1)) return 'אתמול';
  const [, month, day] = dateKey.split('-');
  return month && day ? `${day}/${month}` : dateKey;
}

/** `08:00–12:00`, or just the start when no estimate was given. */
export function formatTaskWindow(task: Task): string {
  if (!task.startTime) return '';
  return task.estEndTime ? `${task.startTime}–${task.estEndTime}` : task.startTime;
}

// ── Visibility ───────────────────────────────────────────────────────

/**
 * The copy of a task a given viewer may read. Internal manager notes are
 * dropped for anyone without `VIEW_ALL_TASKS`; the answer itself comes from
 * the permissions engine, never from here.
 */
export function redactTask(user: User | null | undefined, task: Task): Task {
  if (canViewInternalNotes(user)) return task;
  const { internalNotes: _internalNotes, ...visible } = task;
  return visible as Task;
}

export function redactTasks(user: User | null | undefined, tasks: readonly Task[]): Task[] {
  return tasks.map((task) => redactTask(user, task));
}
